import React, { useMemo, useState } from 'react';
import { useCaeMRC } from '../../../hooks/analytics/useCaeReports.ts';
import { StatCard, StatusChip, WidgetCard } from '../controller/shared.tsx';

type FrequencyFilter = 'all' | string;

/** Report #13 — MRC (Management Review Control) Dashboard. KPI counts + filterable list. */
export const MRCPanel: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeMRC();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);
  const [frequency, setFrequency] = useState<FrequencyFilter>('all');

  const frequencies = useMemo(
    () => Array.from(new Set(rows.map((r) => r.frequency).filter((f): f is string => !!f))).sort(),
    [rows]
  );

  const filtered = useMemo(
    () => (frequency === 'all' ? rows : rows.filter((r) => r.frequency === frequency)),
    [rows, frequency]
  );

  const passed = rows.filter((r) => r.conclusion === 'effective').length;
  const failed = rows.filter((r) => r.conclusion === 'ineffective').length;

  return (
    <WidgetCard
      title="MRC Dashboard"
      description="Management Review Controls — testing status by frequency"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load MRC dashboard.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No MRC controls found."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="analytics-kpi-grid gap-3 mb-4">
        <StatCard title="Total MRC" value={rows.length} color="bg-purple-100 text-purple-600" />
        <StatCard title="Passed" value={passed} color="bg-green-100 text-green-600" />
        <StatCard title="Failed" value={failed} color="bg-red-100 text-red-600" />
      </div>

      {frequencies.length > 0 && (
        <div className="flex items-center gap-2 mb-3 text-sm flex-wrap">
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="radio"
              name="mrc-frequency-filter"
              checked={frequency === 'all'}
              onChange={() => setFrequency('all')}
            />
            <span>All</span>
          </label>
          {frequencies.map((f) => (
            <label key={f} className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="radio"
                name="mrc-frequency-filter"
                checked={frequency === f}
                onChange={() => setFrequency(f)}
              />
              <span className="capitalize">{f}</span>
            </label>
          ))}
        </div>
      )}

      <ul className="analytics-scroll divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {filtered.map((row) => (
          <li key={row.control_id} className="py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {row.control_id} — {row.title}
              </p>
              <p className="text-xs text-gray-500 capitalize">{row.frequency ?? 'No frequency'}</p>
            </div>
            <StatusChip value={row.conclusion} fallback="Not tested" />
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
};

export default MRCPanel;
